import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@material-tailwind/react';
import { FunctionComponent, useState } from 'react';
import toast from 'react-hot-toast';
import { useApi } from '../../../providers/ApiProvider';
import { GoodApiResponse, ErrorApiResponse } from '../../../types/api';
import { Invitation } from '../../../types/EntreprisesRessources';
import { handleError } from '../../../utils/ErrorHandler';
import { AlertInfo } from '../../Alertes';
import { InputEmail } from '../../Inputs/InputEmail';
import { LoadingAbsolute } from '../../LoadingAbsolute';

/**
 * Ce composant permet d'afficher une Modale pour qu'une entreprise puisse inviter un salarié.
 *
 * @param visible - Précise si la modale et ouverte ou non. Provient du parent.
 * @param setVisible - Accesseur qui modifie la visibilité de la modale. Provient du parent.
 * @param handleIndex - Callback qui permet de recharger les données du composant parent. Provient du parent.
 */
export const ModalEntrepriseInvitation: FunctionComponent<{
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleIndex: () => void;
}> = ({ visible, setVisible, handleIndex }) => {
  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * E-mail du salarié que l'on souhaite inviter
   */
  const [emailInvitation, setEmailInvitation] = useState<string>('');

  /**
   * On stocke l'eventuelle erreur.
   */
  const [error, setError] = useState<string | null>(null);

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Ceet méthode effectue l'appel API pour lancer l'ivitation du salarié grâce à son adresse e-mail.
   *
   * @param emailInvitation - Adresse e-mail du salarié que le dirigeant souhaite inviter.
   */
  const handleInvite = (emailInvitation: string) => {
    setApiIsLoading(true);

    client
      .post<GoodApiResponse>(`entreprise/invitations/invite`, { email: emailInvitation })
      .then(() => {
        handleIndex();
        setEmailInvitation('');
        setError(null);
        setVisible(false);
        toast.success(`Invitation envoyée`);
      })
      .catch((error: ErrorApiResponse<Invitation>) => {
        handleError(
          error,
          () => {
            setError(error.response.data.message);
          },
          () => {
            setError(error.message);
          },
        );
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  return (
    <Dialog open={visible} size='xs' handler={setVisible}>
      <DialogHeader>Envoyer une invitation</DialogHeader>
      <DialogBody divider>
        <AlertInfo text="Cette action va envoyer une invitation d'affiliation au salarié." />
        <p className='text-sm mb-2'>Veuillez saisir l'adresse mail de la personne à inviter</p>
        <InputEmail
          value={emailInvitation}
          onChange={(e) => {
            setEmailInvitation(e.target.value);
          }}
        />
        {error && <p className='text-sm text-red-500'>{error}</p>}
      </DialogBody>
      <DialogFooter>
        <Button
          variant='text'
          color='gray'
          onClick={() => {
            setEmailInvitation('');
            setError(null);
            setVisible(false);
          }}
          className='mr-1 !rounded-full'
          disabled={apiIsLoading}>
          Annuler
        </Button>
        <Button
          variant='filled'
          color='orange'
          onClick={() => {
            handleInvite(emailInvitation);
          }}
          disabled={apiIsLoading}>
          Confirmer
        </Button>
      </DialogFooter>
      {apiIsLoading ? <LoadingAbsolute /> : ''}
    </Dialog>
  );
};
